import React from 'react';
import { motion } from 'framer-motion';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
    Building2, UserRound, Home, Gauge, Sparkles, Bell,
    Target, Activity, Rocket, Eye, ShieldCheck, HeartHandshake,
} from 'lucide-react';

const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif" };
const MONO = { fontFamily: "'JetBrains Mono', monospace" };

const ROLES = [
    {
        id: 'dirigeant',
        tab: "Dirigeant d'agence",
        tabShort: 'Dirigeant',
        tabIcon: Building2,
        kicker: 'Interface dirigeant',
        title: 'Pilotez en toute clarté',
        img: '/dash-dirigeant.png',
        alt: 'Cockpit dirigeant Homly — KPIs, équipe et pilotage',
        points: [
            { icon: Gauge, text: 'Tous vos indicateurs de transaction, lisibles en 30 secondes — du jour à l’année.' },
            { icon: Sparkles, text: 'Synthèse IA chaque lundi : votre semaine analysée, plan d’action prêt.' },
            { icon: Bell, text: 'Alertes en temps réel sur les mandats à risque et les négociateurs qui décrochent.' },
        ],
    },
    {
        id: 'negociateur',
        tab: 'Négociateur',
        tabShort: 'Négociateur',
        tabIcon: UserRound,
        kicker: 'Interface négociateur',
        title: 'Sachez quoi faire, chaque matin',
        img: '/dash-negociateur.png',
        alt: 'Cockpit négociateur Homly — priorités du jour triées par l’IA',
        points: [
            { icon: Target, text: 'Vos actions du jour, triées par impact business par l’IA.' },
            { icon: Activity, text: 'Votre progression vers l’objectif, suivie en temps réel.' },
            { icon: Rocket, text: 'Un coach IA qui détecte les mandats à relancer et vous fait progresser.' },
        ],
    },
    {
        id: 'vendeur',
        tab: 'Vendeur',
        tabShort: 'Vendeur',
        tabIcon: Home,
        kicker: 'Interface vendeur',
        title: 'Une transparence qui rassure',
        img: '/dash-vendeur.png',
        alt: 'Espace vendeur Homly — suivi de la vente en temps réel',
        points: [
            { icon: Eye, text: 'Votre client suit l’avancement de sa vente en temps réel.' },
            { icon: ShieldCheck, text: 'Chaque visite, chaque retour, chaque action lui est visible.' },
            { icon: HeartHandshake, text: 'Moins d’appels entrants, plus de confiance entretenue.' },
        ],
    },
];

const RolePanel = ({ role }) => (
    <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        <div className="order-2 lg:order-1">
            <p className="text-xs uppercase tracking-widest text-[#C084FC]" style={MONO}>{role.kicker}</p>
            <h3 className="mt-3 text-2xl sm:text-3xl font-bold text-white" style={DISPLAY}>{role.title}</h3>
            <ul className="mt-6 space-y-4">
                {role.points.map((p, i) => {
                    const Icon = p.icon;
                    return (
                        <li key={i} className="flex items-start gap-3">
                            <span className="mt-0.5 shrink-0 w-9 h-9 rounded-lg bg-white/[0.04] border border-white/[0.06] flex items-center justify-center">
                                <Icon className="w-4 h-4 text-[#FF914D]" strokeWidth={1.9} />
                            </span>
                            <span className="text-sm text-[#C9C5D2] leading-relaxed">{p.text}</span>
                        </li>
                    );
                })}
            </ul>
        </div>
        <div className="order-1 lg:order-2 relative">
            <div className="absolute -inset-3 rounded-[24px] bg-gradient-to-tr from-[#681793]/25 to-[#FF914D]/15 blur-3xl" />
            <div className="relative rounded-2xl overflow-hidden border border-white/[0.08] shadow-2xl shadow-black/60 bg-[#0C0A10]">
                <img src={role.img} alt={role.alt} className="w-full h-auto block" loading="lazy" />
            </div>
        </div>
    </div>
);

/**
 * ConvInterfaces — "Une plateforme, trois interfaces".
 * Onglets Dirigeant / Négociateur / Vendeur, mockups statiques
 * (sans overlay ni animation intrusive).
 */
export const ConvInterfaces = () => {
    return (
        <section className="relative py-20 md:py-32" data-testid="interfaces-section">
            <div className="max-w-6xl mx-auto px-6 md:px-12">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.6 }}
                    className="text-center"
                >
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-white" style={DISPLAY}>
                        Une plateforme,{' '}
                        <span className="bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent">trois interfaces.</span>
                    </h2>
                    <p className="mt-4 text-base md:text-lg text-[#A19DAB] max-w-2xl mx-auto">
                        Chaque rôle a son espace dédié — pensé pour ce qu’il a vraiment besoin de voir.
                    </p>
                </motion.div>

                <Tabs defaultValue="dirigeant" className="mt-10">
                    <TabsList className="mx-auto grid h-auto w-full max-w-sm grid-cols-3 items-stretch gap-1 rounded-2xl border border-white/10 bg-[#0C0A10] p-1.5 shadow-lg shadow-black/40 sm:flex sm:w-fit sm:max-w-none sm:items-center sm:rounded-full" data-testid="interfaces-tabs">
                        {ROLES.map((r) => {
                            const Icon = r.tabIcon;
                            return (
                                <TabsTrigger
                                    key={r.id}
                                    value={r.id}
                                    data-testid={`interface-tab-${r.id}`}
                                    className="inline-flex items-center justify-center gap-2 rounded-xl px-2 py-2.5 text-xs font-medium leading-none whitespace-nowrap text-[#A19DAB] transition-colors hover:text-white data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#681793] data-[state=active]:to-[#FF914D] data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-[#681793]/30 sm:rounded-full sm:px-6 sm:text-sm"
                                >
                                    <Icon className="hidden sm:block w-4 h-4 shrink-0" strokeWidth={2} />
                                    <span className="sm:hidden">{r.tabShort}</span>
                                    <span className="hidden sm:inline">{r.tab}</span>
                                </TabsTrigger>
                            );
                        })}
                    </TabsList>

                    {ROLES.map((r) => (
                        <TabsContent key={r.id} value={r.id} className="mt-10" data-testid={`interface-panel-${r.id}`}>
                            <RolePanel role={r} />
                        </TabsContent>
                    ))}
                </Tabs>
            </div>
        </section>
    );
};
